/**
 * iter312 — Super Admin platform settings page.
 *
 * Currently exposes:
 *   • Platform favicon upload — used on platform hosts
 *     (my.vyomos.io, vyomos.io, www.vyomos.io) so the
 *     browser tab icon shows the SaaS-platform logo instead of
 *     defaulting to whatever was bundled into /public/das-logo.png.
 *
 * Future: support email, platform terms, default new-tenant brand
 * theme, billing-portal copy, etc.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import SuperAdminByokPanel from "./SuperAdminByokPanel"; // iter334cf

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + "/api";
const ORIGIN = (typeof window !== "undefined" && window.location ? window.location.origin : "");

const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminSettings() {
  const [favicon, setFavicon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/platform/favicon`);
      setFavicon(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load platform favicon");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Favicon must be 2MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/platform/favicon`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Platform favicon updated");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Revert to the bundled default favicon?")) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/platform/favicon`, { headers: authHeader() });
      toast.success("Reverted to default favicon");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear favicon");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="super-admin-settings-page">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Platform Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Defaults that apply to the SaaS platform itself (Vyomos) — used on{" "}
          <span className="font-mono">my.vyomos.io</span> and other platform hosts.
        </p>
      </header>

      <section
        className="bg-white border border-slate-200 rounded-lg p-6 space-y-4"
        data-testid="platform-favicon-card"
      >
        <h3 className="font-semibold flex items-center gap-2">
          <ImageIcon className="w-4 h-4" /> Platform favicon
        </h3>
        <p className="text-xs text-slate-500">
          Used as the browser tab icon on platform hosts. Tenant hosts (custom
          domains and subdomains) always show the tenant&apos;s own logo. Accepts{" "}
          <span className="font-mono">.ico</span>, <span className="font-mono">.png</span>,{" "}
          <span className="font-mono">.svg</span> — square recommended, max 2MB.
        </p>

        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            ) : favicon?.favicon_url ? (
              <img
                src={`${ORIGIN}${favicon.favicon_url}`}
                alt="Platform favicon"
                className="max-w-full max-h-full object-contain"
                data-testid="platform-favicon-preview"
              />
            ) : (
              <span className="text-xs text-slate-400 px-2 text-center">
                Default
              </span>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/x-icon,image/png,image/svg+xml,image/jpeg,image/webp"
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
                data-testid="platform-favicon-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="platform-favicon-upload-btn"
                className="bg-slate-900 text-white rounded px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-slate-800 disabled:opacity-50"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload favicon
              </button>
              {favicon?.favicon_url && (
                <button
                  onClick={handleClear}
                  disabled={clearing}
                  data-testid="platform-favicon-clear-btn"
                  className="ml-3 border border-slate-300 rounded px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50"
                >
                  {clearing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Revert
                </button>
              )}
            </div>
            {favicon?.favicon_url && (
              <p className="text-[11px] text-slate-500 font-mono">
                {favicon.favicon_url}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* iter334cf — BYOK Stripe + Resend + suspension config */}
      <section
        className="bg-white border border-slate-200 rounded-lg p-6"
        data-testid="platform-byok-card"
      >
        <div className="mb-4">
          <h3 className="font-semibold text-lg">Platform Integrations</h3>
          <p className="text-xs text-slate-500 mt-1">
            BYOK keys used by the platform itself to charge tenants and send billing emails.
          </p>
        </div>
        <SuperAdminByokPanel />
      </section>
    </div>
  );
}
